import React, { useState, useEffect } from 'react';
import { paymentsApi, invoicesApi } from '../../services/api';
import toast from 'react-hot-toast';
import { HiX } from 'react-icons/hi';
import { usePermissions } from '../../hooks/usePermissions';
import { formatValidationErrors } from '../../utils/errorHandler';
import { todayYmdIST, formatDateForInputIST, formatCalendarDateDMY } from '../../utils/timezoneDisplay';

const PaymentForm = ({ payment, invoice, invoiceType, onClose, onSuccess }) => {
  const { canCreate, canUpdate } = usePermissions();
  const [loading, setLoading] = useState(false);
  const [pendingInvoices, setPendingInvoices] = useState([]);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [formData, setFormData] = useState({
    invoice_id: invoice?.id || '',
    amount: '',
    payment_date: todayYmdIST(),
    payment_mode: 'cash',
    invoice_type: 'assessment',
    transaction_reference: '',
    notes: ''
  });

  useEffect(() => {
    // Fetch pending invoices if not editing a payment
    if (!payment) {
      if (invoice) {
        // Invoice prop provided - determine type and fetch
        const invoiceTypeValue = (invoice.payment_type === 'assessment' && invoice.assessment_id) ? 'assessment' : 'patient';
        setFormData(prev => ({
          ...prev,
          invoice_id: invoice.id,
          amount: invoice.balance_amount || '',
          invoice_type: invoiceTypeValue
        }));
        setSelectedInvoice(invoice);
        fetchPendingInvoices(invoiceTypeValue);
      } else if (invoiceType) {
        // InvoiceType prop provided - use it
        setFormData(prev => ({
          ...prev,
          invoice_type: invoiceType
        }));
        fetchPendingInvoices(invoiceType);
      } else {
        // No props - fetch assessment invoices by default
        fetchPendingInvoices('assessment');
      }
    }
    
    if (payment) {
      // Load payment data for editing
      setFormData({
        invoice_id: payment.invoice_id || '',
        amount: payment.amount || '',
        payment_date: formatDateForInputIST(payment.payment_date) || todayYmdIST(),
        payment_mode: payment.payment_mode || 'cash',
        invoice_type: payment.invoice_type || '',
        transaction_reference: payment.transaction_reference || '',
        notes: payment.notes || ''
      });
      // Fetch invoice details for the payment
      if (payment.invoice_id) {
        fetchInvoiceDetails(payment.invoice_id);
      }
    } else if (invoiceType) {
      // If invoiceType prop is provided, set it and fetch invoices
      setFormData(prev => ({
        ...prev,
        invoice_type: invoiceType
      }));
      // If invoiceType prop is provided, set it and fetch invoices
      if (invoiceType) {
        setFormData(prev => ({
          ...prev,
          invoice_type: invoiceType
        }));
        fetchPendingInvoices(invoiceType);
      } else {
        // Otherwise fetch assessment invoices by default
        fetchPendingInvoices('assessment');
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [payment, invoice, invoiceType]);

  const fetchInvoiceDetails = async (invoiceId) => {
    try {
      const response = await invoicesApi.getById(invoiceId);
      setSelectedInvoice(response.data.data);
    } catch (error) {
      // Handle error silently - invoice info might already be in payment data
    }
  };

  const fetchPendingInvoices = async (type = null) => {
    try {
      const params = {};
      if (type) {
        params.invoice_type = type;
      }
      const response = await paymentsApi.getPending(params);
      setPendingInvoices(response.data.data || []);
    } catch (error) {
      toast.error('Failed to fetch pending invoices');
    }
  };

  const handleInvoiceChange = (invoiceId) => {
    const invoice = pendingInvoices.find(inv => inv.id === parseInt(invoiceId));
    setSelectedInvoice(invoice);
    
    // Determine invoice type based on invoice payment_type and assessment_id
    let invoiceType = '';
    if (invoice) {
      if (invoice.payment_type === 'assessment' && invoice.assessment_id) {
        invoiceType = 'assessment';
      } else {
        invoiceType = 'patient';
      }
    }
    
    setFormData(prev => ({
      ...prev,
      invoice_id: invoiceId,
      amount: invoice ? invoice.balance_amount : '',
      invoice_type: invoiceType
    }));
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Check permissions
    if (payment && !canUpdate('payments')) {
      toast.error('You do not have permission to update payments');
      return;
    }
    if (!payment && !canCreate('payments')) {
      toast.error('You do not have permission to create payments');
      return;
    }

    setLoading(true);

    try {
      // Validate amount
      const amount = parseFloat(formData.amount);
      if (!amount || amount <= 0) {
        toast.error('Please enter a valid payment amount');
        setLoading(false);
        return;
      }

      if (!payment && selectedInvoice) {
        const remainingBalance = parseFloat(selectedInvoice.balance_amount);
        if (amount > remainingBalance) {
          toast.error(`Payment amount cannot exceed remaining balance of ₹${remainingBalance.toFixed(2)}`);
          setLoading(false);
          return;
        }
      }

      if (payment) {
        // Update payment
        const submitData = {
          amount: amount,
          payment_date: formData.payment_date,
          payment_mode: formData.payment_mode,
          invoice_type: formData.invoice_type || null,
          transaction_reference: formData.transaction_reference || null,
          notes: formData.notes || null
        };

        await paymentsApi.update(payment.id, submitData);
        toast.success('Payment updated successfully');
      } else {
        // Create new payment
        const submitData = {
          invoice_id: parseInt(formData.invoice_id),
          amount: amount,
          payment_date: formData.payment_date,
          payment_mode: formData.payment_mode,
          invoice_type: formData.invoice_type || null,
          transaction_reference: formData.transaction_reference || null,
          notes: formData.notes || null
        };

        await paymentsApi.record(submitData);
        toast.success('Payment recorded successfully');
      }
      onSuccess();
      onClose();
    } catch (error) {
      toast.error(formatValidationErrors(error));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-shell flex items-center justify-center" onClick={onClose}>
      <div className="modal-panel max-w-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="p-4 sm:p-6 border-b border-gray-200 flex items-center justify-between sticky top-0 bg-white z-10">
          <h3 className="text-lg font-semibold">
            {payment ? 'Edit Payment' : 'Record Payment'}
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <HiX className="w-6 h-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-4 sm:p-6 space-y-5 sm:space-y-6 overflow-y-auto">
          {/* Invoice Type Selection */}
          {!payment && !invoice && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Invoice Type <span className="text-red-500">*</span>
              </label>
              <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
                <label className="flex items-center cursor-pointer">
                  <input
                    type="radio"
                    name="invoice_type"
                    value="assessment"
                    checked={(formData.invoice_type || invoiceType || '') === 'assessment'}
                    onChange={(e) => {
                      const newType = e.target.value;
                      setFormData(prev => ({
                        ...prev,
                        invoice_type: newType,
                        invoice_id: '' // Clear invoice selection when type changes
                      }));
                      setSelectedInvoice(null);
                      // Fetch invoices for the selected type
                      fetchPendingInvoices(newType || null);
                    }}
                    className="w-4 h-4 text-primary-600 focus:ring-primary-500 focus:ring-2"
                  />
                  <span className="ml-2 text-sm text-gray-700">Assessment Fees</span>
                </label>
                <label className="flex items-center cursor-pointer">
                  <input
                    type="radio"
                    name="invoice_type"
                    value="patient"
                    checked={(formData.invoice_type || invoiceType || '') === 'patient'}
                    onChange={(e) => {
                      const newType = e.target.value;
                      setFormData(prev => ({
                        ...prev,
                        invoice_type: newType,
                        invoice_id: '' // Clear invoice selection when type changes
                      }));
                      setSelectedInvoice(null);
                      // Fetch invoices for the selected type
                      fetchPendingInvoices(newType || null);
                    }}
                    className="w-4 h-4 text-primary-600 focus:ring-primary-500 focus:ring-2"
                  />
                  <span className="ml-2 text-sm text-gray-700">Patient Fees</span>
                </label>
              </div>
              <p className="text-xs text-gray-500 mt-1">Select the type of invoice you want to make a payment for</p>
            </div>
          )}

          {/* Invoice Selection */}
          {!payment && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Invoice <span className="text-red-500">*</span>
              </label>
              <select
                name="invoice_id"
                value={formData.invoice_id}
                onChange={(e) => handleInvoiceChange(e.target.value)}
                required
                disabled={!!invoice || (!formData.invoice_type && !invoiceType)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent disabled:bg-gray-100"
              >
                <option value="">{!formData.invoice_type && !invoiceType ? 'Select Invoice Type First' : 'Select Invoice'}</option>
                {pendingInvoices.map((inv) => {
                  // Build display text based on invoice type
                  let displayText = `${inv.invoice_number}`;
                  
                  if (inv.patient_name) {
                    displayText += ` - Patient: ${inv.patient_name}`;
                  } else if (inv.lead_name) {
                    displayText += ` - Lead: ${inv.lead_name}`;
                  } else if (inv.assessment_date) {
                    displayText += ` - Assessment: ${formatCalendarDateDMY(inv.assessment_date)}`;
                  }
                  
                  displayText += ` (Balance: ₹${parseFloat(inv.balance_amount).toFixed(2)})`;
                  
                  return (
                    <option key={inv.id} value={inv.id}>
                      {displayText}
                    </option>
                  );
                })}
              </select>
              {selectedInvoice && (
                <div className="mt-2 p-3 bg-gray-50 rounded-lg text-sm">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {selectedInvoice.patient_name ? (
                      <div>
                        <span className="text-gray-600">Patient:</span>
                        <span className="ml-2 font-medium">{selectedInvoice.patient_name}</span>
                      </div>
                    ) : selectedInvoice.lead_name ? (
                      <div>
                        <span className="text-gray-600">Lead:</span>
                        <span className="ml-2 font-medium">{selectedInvoice.lead_name}</span>
                      </div>
                    ) : selectedInvoice.assessment_date ? (
                      <div>
                        <span className="text-gray-600">Assessment Date:</span>
                        <span className="ml-2 font-medium">{formatCalendarDateDMY(selectedInvoice.assessment_date)}</span>
                      </div>
                    ) : null}
                    <div>
                      <span className="text-gray-600">Total Amount:</span>
                      <span className="ml-2 font-medium">₹{parseFloat(selectedInvoice.total_amount).toFixed(2)}</span>
                    </div>
                    <div>
                      <span className="text-gray-600">Paid Amount:</span>
                      <span className="ml-2 font-medium">₹{parseFloat(selectedInvoice.paid_amount || 0).toFixed(2)}</span>
                    </div>
                    <div>
                      <span className="text-gray-600">Balance:</span>
                      <span className="ml-2 font-medium text-amber-600">₹{parseFloat(selectedInvoice.balance_amount).toFixed(2)}</span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {payment && (
            <div className="p-3 bg-gray-50 rounded-lg">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
                <div>
                  <span className="text-gray-600">Invoice:</span>
                  <span className="ml-2 font-medium text-primary-600">{payment.invoice_number}</span>
                </div>
                <div>
                  <span className="text-gray-600">Receipt:</span>
                  <span className="ml-2 font-medium">{payment.receipt_number}</span>
                </div>
                {payment.invoice_total && (
                  <>
                    <div>
                      <span className="text-gray-600">Invoice Total:</span>
                      <span className="ml-2 font-medium">₹{parseFloat(payment.invoice_total).toFixed(2)}</span>
                    </div>
                    <div>
                      <span className="text-gray-600">Current Payment:</span>
                      <span className="ml-2 font-medium text-green-600">₹{parseFloat(payment.amount).toFixed(2)}</span>
                    </div>
                  </>
                )}
              </div>
              <p className="text-xs text-amber-600 mt-2">
                ⚠️ Changing the amount will update the invoice balance automatically
              </p>
            </div>
          )}

          {/* Payment Amount */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Payment Amount (₹) <span className="text-red-500">*</span>
            </label>
            <input
              type="number"
              name="amount"
              value={formData.amount}
              onChange={handleChange}
              required
              min="0.01"
              step="0.01"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              placeholder="0.00"
            />
            {selectedInvoice && (
              <p className="mt-1 text-xs text-gray-500">
                Maximum: ₹{parseFloat(selectedInvoice.balance_amount).toFixed(2)}
              </p>
            )}
          </div>

          {/* Payment Date */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Payment Date <span className="text-red-500">*</span>
            </label>
            <input
              type="date"
              name="payment_date"
              value={formData.payment_date}
              onChange={handleChange}
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
          </div>

          {/* Payment Mode */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Payment Mode <span className="text-red-500">*</span>
            </label>
            <select
              name="payment_mode"
              value={formData.payment_mode}
              onChange={handleChange}
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            >
              <option value="cash">Cash</option>
              <option value="upi">UPI</option>
              <option value="bank_transfer">Bank Transfer</option>
              <option value="online_link">Online Link</option>
              <option value="card">Card</option>
              <option value="cheque">Cheque</option>
            </select>
          </div>


          {/* Transaction Reference */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Transaction Reference
            </label>
            <input
              type="text"
              name="transaction_reference"
              value={formData.transaction_reference}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              placeholder="Transaction ID, UPI reference, cheque number, etc."
            />
          </div>

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Notes
            </label>
            <textarea
              name="notes"
              value={formData.notes}
              onChange={handleChange}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              placeholder="Additional payment notes..."
            />
          </div>

          {/* Form Actions */}
          <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2 sm:gap-3 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="w-full sm:w-auto px-4 py-2.5 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading || (!payment && !formData.invoice_id)}
              className="w-full sm:w-auto px-4 py-2.5 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (payment ? 'Updating...' : 'Recording...') : (payment ? 'Update Payment' : 'Record Payment')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PaymentForm;

